import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCalendarTypes } from '~/src/hooks/useCalendarTypes';
import { calendarService } from '~/src/services/calendarService';
import checkIcon from '~/static/assets/icons/landing-page/check-icon.svg';
import closeIcon from '~/static/assets/icons/landing-page/close-icon.svg';
import downloadIcon from '~/static/assets/icons/landing-page/download-icon.svg';
import editIcon from '~/static/assets/icons/landing-page/edit-icon.svg';
// Asset imports - bundler handles path resolution
import warningIcon from '~/static/assets/icons/landing-page/warning-icon.svg';
import { TOS_PDF_URL } from '../../constants/common';
import { useSettings, useTOS } from '../../contexts/SettingsContext';
import { useUser } from '../../contexts/UserContext';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useSettingsManager } from '../../hooks/useSettingsManager';
import {
  buildCalendarOauthCloseUrl,
  type CalendarProvider,
  isTrustedCalendarMessageOrigin,
  notifyCalendarOauthResult,
} from '../../lib/calendarOauth';
import {
  areMeetingPreferencesEqual,
  getLocalMeetingPreferences,
} from '../../services/meetingPreferencesSync';
import { DEVICE_LS_KEYS, updateJitsiLocalStorageStartMuted } from '../../utils/deviceUtils';
import { DevicesTab } from './DevicesTab';
import { getProviderDisplayName, getProviderIcon } from './helpers/getProviderInfo';
import { getSignInButtonClasses } from './helpers/getSignInButtonClasses';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWindowClose?: () => void;
  initialTab?: SettingsTab;
}

type SettingsTab = 'devices' | 'profile' | 'calendar' | 'meetings' | 'terms';

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  onWindowClose: _onWindowClose,
  initialTab,
}) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('devices');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [userHasMadeChanges, setUserHasMadeChanges] = useState(false);
  const [hasViewedTos, setHasViewedTos] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [calendarReconnectErrors, setCalendarReconnectErrors] = useState<{
    google: string | null;
    microsoft: string | null;
  }>({
    google: null,
    microsoft: null,
  });

  // Use the settings manager hook (for local settings like devices)
  const {
    settings,
    isLoading,
    isSaving,
    isValid: _isValid,
    errors,
    hasUnsavedChanges,
    updateSetting,
    saveSettings,
    resetSettings: _resetSettings,
    revertToPersisted,
    clearErrors,
  } = useSettingsManager();
  const { handleComponentError } = useErrorHandler('SettingsModal');
  const { data: calendarTypes, refetch: refetchCalendarTypes } = useCalendarTypes();

  // Get authentication data
  const { isAuthenticated, userName, userEmail, userInfo } = useUser();

  // Use new settings context for backend-synced profile data
  const {
    profile: backendProfile,
    isLoadingProfile,
    profileError,
    updateProfile: updateBackendProfile,
    isBackendConnected,
  } = useSettings();

  // Use TOS hook for Terms of Service data
  const { tosAcceptance, isLoadingTOS, acceptTOS, refetchTOS } = useTOS();

  // TOS acceptance local state for immediate UI feedback
  const acceptedDate = tosAcceptance?.accepted;
  const hasAcceptedTos = !!acceptedDate && acceptedDate !== 'Never';
  const [localAccepted, setLocalAccepted] = useState(hasAcceptedTos);
  const [isAccepting, setIsAccepting] = useState(false);

  // Check if there are unsaved TOS changes (either accepting OR unaccepting)
  const hasTosChanges = localAccepted !== hasAcceptedTos;

  // Sync local state with backend state
  useEffect(() => {
    setLocalAccepted(hasAcceptedTos);
  }, [hasAcceptedTos]);

  // Sync from localStorage and jitsiLocalStorage when modal opens
  // This catches changes that happened while modal was closed (e.g., device changes in Jitsi meeting)
  const hasSyncedOnOpen = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset the flag when modal closes so next open will sync
      hasSyncedOnOpen.current = false;
      return;
    }

    // Only sync once per modal open, not when dependencies change while open
    if (hasSyncedOnOpen.current) {
      console.log('[Settings] Skipping sync - already synced on this modal open');
      return;
    }

    const syncDevices = async () => {
      console.log('[Settings] Modal opened - syncing from localStorage and jitsiLocalStorage');

      hasSyncedOnOpen.current = true;

      try {
        let storedCamera = localStorage.getItem(DEVICE_LS_KEYS.camera);
        let storedMicrophone = localStorage.getItem(DEVICE_LS_KEYS.microphone);
        let storedAudioOutput = localStorage.getItem(DEVICE_LS_KEYS.audioOutput);

        // Also check jitsiLocalStorage (Jitsi's internal storage - takes precedence)
        try {
          const jitsiLs = localStorage.getItem('jitsiLocalStorage');
          if (jitsiLs) {
            const jitsiData = JSON.parse(jitsiLs);
            const baseSettings = jitsiData['features/base/settings'];
            if (baseSettings) {
              const jitsiSettings = JSON.parse(baseSettings);

              // Prioritize Jitsi values as they're the source of truth during/after meetings
              if (jitsiSettings.userSelectedCameraDeviceId) {
                storedCamera = jitsiSettings.userSelectedCameraDeviceId;
                console.log('[Settings] Found camera in jitsiLocalStorage:', storedCamera);
              }
              if (jitsiSettings.userSelectedMicDeviceId) {
                storedMicrophone = jitsiSettings.userSelectedMicDeviceId;
                console.log('[Settings] Found microphone in jitsiLocalStorage:', storedMicrophone);
              }
              if (jitsiSettings.userSelectedAudioOutputDeviceId) {
                storedAudioOutput = jitsiSettings.userSelectedAudioOutputDeviceId;
                console.log(
                  '[Settings] Found audio output in jitsiLocalStorage:',
                  storedAudioOutput,
                );
              }
            }
          }
        } catch (e) {
          console.warn('[Settings] Error reading jitsiLocalStorage:', e);
        }

        console.log('[Settings] Values from storage:', {
          camera: storedCamera,
          microphone: storedMicrophone,
          audioOutput: storedAudioOutput,
        });

        console.log('[Settings] Current values:', {
          camera: settings.devices.selectedCamera,
          microphone: settings.devices.selectedMicrophone,
          audioOutput: settings.devices.selectedAudioOutput,
        });

        // Track if we made any programmatic updates
        let didUpdate = false;

        // Sync if values differ
        if (storedCamera && storedCamera !== settings.devices.selectedCamera) {
          console.log('[Settings] Syncing camera from storage:', storedCamera);
          await updateSetting('devices.selectedCamera', storedCamera);
          didUpdate = true;
        }
        if (storedMicrophone && storedMicrophone !== settings.devices.selectedMicrophone) {
          console.log('[Settings] Syncing microphone from storage:', storedMicrophone);
          await updateSetting('devices.selectedMicrophone', storedMicrophone);
          didUpdate = true;
        }
        if (storedAudioOutput && storedAudioOutput !== settings.devices.selectedAudioOutput) {
          console.log('[Settings] Syncing audio output from storage:', storedAudioOutput);
          await updateSetting('devices.selectedAudioOutput', storedAudioOutput);
          didUpdate = true;
        }

        // If we made programmatic updates, save them immediately to reset hasUnsavedChanges
        if (didUpdate) {
          console.log('[Settings] Committing programmatic sync to prevent "unsaved changes" flag');
          await saveSettings();
          setUserHasMadeChanges(false);
        }
      } catch (e) {
        console.error('[Settings] Error syncing from storage on open:', e);
      }
    };

    syncDevices();
  }, [isOpen, settings, updateSetting, saveSettings]);

  // Reset local state when modal opens and refetch TOS data
  useEffect(() => {
    if (isOpen) {
      // Refetch TOS acceptance from backend to get latest state
      refetchTOS();
      setLocalAccepted(hasAcceptedTos);
      setHasViewedTos(false);
      setActiveTab(initialTab ?? 'devices');
    }
  }, [isOpen, refetchTOS, hasAcceptedTos, initialTab]);

  // Extract settings for easier access
  const {
    selectedCamera,
    selectedMicrophone,
    selectedAudioOutput,
    startWithAudioMuted,
    startWithVideoMuted,
  } = settings.devices;

  const { displayName, gravatarEmail } = settings.profile;

  // Compute full name from authentication data (first + last name)
  const authFullName = React.useMemo(() => {
    if (!(isAuthenticated && userInfo)) {
      return '';
    }

    // Try to get full name from given_name + family_name
    if (userInfo.given_name && userInfo.family_name) {
      return `${userInfo.given_name} ${userInfo.family_name}`;
    }

    // Fall back to name field or userName
    return userInfo.name || userInfo.full_name || userName || '';
  }, [isAuthenticated, userInfo, userName]);

  // Local state for profile editing
  // If authenticated, use auth data; otherwise use backend or local storage
  const [localProfile, setLocalProfile] = useState({
    displayName: isAuthenticated ? authFullName : backendProfile?.displayName || displayName || '',
    gravatarEmail: isAuthenticated
      ? userEmail
      : backendProfile?.gravatarEmail || gravatarEmail || '',
  });

  // Sync backend profile to local state when loaded
  useEffect(() => {
    if (isAuthenticated) {
      // If authenticated, always use auth data (read-only)
      setLocalProfile({
        displayName: authFullName,
        gravatarEmail: userEmail,
      });
    } else if (backendProfile) {
      // If not authenticated, use backend profile (editable)
      setLocalProfile({
        displayName: backendProfile.displayName || '',
        gravatarEmail: backendProfile.gravatarEmail || '',
      });
    }
  }, [backendProfile, isAuthenticated, authFullName, userEmail]);

  useEffect(() => {
    if (isOpen && hasUnsavedChanges) {
      setUserHasMadeChanges(true);
    } else if (!hasUnsavedChanges) {
      setUserHasMadeChanges(false);
    }
  }, [hasUnsavedChanges, isOpen]);

  // Reset user changes flag when modal opens
  useEffect(() => {
    if (isOpen) {
      setUserHasMadeChanges(false);
    }
  }, [isOpen]);

  // Auto-save programmatic changes (device sync, validation, etc.)
  // If hasUnsavedChanges is true but the user hasn't explicitly interacted, auto-save
  const AutoSaveDelayMs = 1000;
  const shouldAutoSave = isOpen && hasUnsavedChanges && !userHasMadeChanges;
  useEffect(() => {
    if (!shouldAutoSave) {
      return;
    }

    const timer = setTimeout(async () => {
      console.log('[Settings] Auto-saving programmatic changes');
      await saveSettings();
    }, AutoSaveDelayMs);
    return () => clearTimeout(timer);
  }, [shouldAutoSave, saveSettings]);

  // Clear the userHasMadeChanges flag when changes are saved
  useEffect(() => {
    if (!hasUnsavedChanges) {
      setUserHasMadeChanges(false);
    }
  }, [hasUnsavedChanges]);

  // Show error message with delay to prevent brief flashes during save
  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    const ErrorMessageDelayMs = 500;

    if (hasErrors && !isSaving) {
      // Delay showing error message to prevent flashing during save
      const timer = setTimeout(() => {
        setShowErrorMessage(true);
      }, ErrorMessageDelayMs);
      return () => clearTimeout(timer);
    }
    // Hide immediately when errors are cleared
    setShowErrorMessage(false);
  }, [errors, isSaving]);

  // Handle profile field changes
  const handleProfileChange = (field: 'displayName' | 'gravatarEmail', value: string) => {
    setLocalProfile((prev) => ({ ...prev, [field]: value }));
  };

  // Save profile to backend
  const handleProfileSave = async () => {
    try {
      // Update local settings first (always works)
      await updateSetting('profile.displayName', localProfile.displayName);
      await updateSetting('profile.gravatarEmail', localProfile.gravatarEmail);

      // This runs on every modal save, including device-only edits, so skip the
      // backend write unless the profile fields themselves changed (SM-1168).
      const profileChanged =
        localProfile.displayName !== (backendProfile?.displayName ?? '') ||
        localProfile.gravatarEmail !== (backendProfile?.gravatarEmail ?? '');

      if (!isBackendConnected) {
        console.log('[SettingsModal] Backend not connected, profile saved locally only');
      } else if (profileChanged) {
        await updateBackendProfile(localProfile);
        console.log('[SettingsModal] Profile synced to backend');
      }

      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      handleComponentError(error as Error, 'save_profile');
    }
  };

  const handleCancel = useCallback(() => {
    if (userHasMadeChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes. Are you sure you want to discard them?',
      );
      if (!confirmDiscard) {
        return;
      }
    }

    // Revert device (and other) settings to last persisted state so next open shows saved values
    revertToPersisted();

    // Reset local TOS checkbox state if not saved
    if (localAccepted && !hasAcceptedTos) {
      setLocalAccepted(false);
    }

    clearErrors();
    onClose();
  }, [userHasMadeChanges, revertToPersisted, localAccepted, hasAcceptedTos, clearErrors, onClose]);

  const modalRef = useRef<HTMLDivElement>(null);
  // Local ref for modal container only

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleCancel]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on a select element or its options
      if (target && (target.tagName === 'SELECT' || target.tagName === 'OPTION')) {
        return;
      }

      // Improved check: Only close if the actual clicked element is the overlay
      // This prevents issues where checking .contains() fails (e.g. detached elements, portals)
      if (target.classList.contains('settingsModalOverlay')) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleCancel]);

  // Do not early-return before hooks. Render gating is handled below just before JSX.

  // All device preview/meter/test logic moved to DevicesTab

  // Ensure we never propagate an object as a device id
  const sanitizeDeviceId = useCallback((value: unknown): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      // Guard against coerced object strings
      return value.startsWith('[object') ? '' : value;
    }
    if (
      typeof value === 'object' &&
      // biome-ignore lint/suspicious/noExplicitAny: runtime-typed device constraint object
      (value as any).deviceId &&
      // biome-ignore lint/suspicious/noExplicitAny: runtime-typed device constraint object
      typeof (value as any).deviceId === 'string'
    ) {
      // biome-ignore lint/suspicious/noExplicitAny: runtime-typed device constraint object
      const id = (value as any).deviceId as string;
      return id.startsWith('[object') ? '' : id;
    }
    return '';
  }, []);

  // Device settings are now managed by the settings service
  // This effect is kept for backward compatibility with existing localStorage entries
  // Only runs once on mount to migrate legacy settings
  useEffect(() => {
    const migrationKey = 'settings.legacy.migrated';
    try {
      // Check if migration already happened
      const alreadyMigrated = localStorage.getItem(migrationKey);
      if (alreadyMigrated === 'true') {
        return;
      }

      const savedCamera = localStorage.getItem(DEVICE_LS_KEYS.camera);
      const savedMicrophone = localStorage.getItem(DEVICE_LS_KEYS.microphone);
      const savedAudioOutput = localStorage.getItem(DEVICE_LS_KEYS.audioOutput);

      console.log('[Settings] Checking for legacy localStorage entries:', {
        savedCamera,
        savedMicrophone,
        savedAudioOutput,
      });

      let migrated = false;
      // Migrate legacy settings to new settings service if they exist and are different
      if (
        savedCamera &&
        savedCamera !== '[object Object]' &&
        savedCamera !== 'undefined' &&
        savedCamera !== selectedCamera
      ) {
        updateSetting('devices.selectedCamera', savedCamera);
        migrated = true;
      }
      if (
        savedMicrophone &&
        savedMicrophone !== '[object Object]' &&
        savedMicrophone !== 'undefined' &&
        savedMicrophone !== selectedMicrophone
      ) {
        updateSetting('devices.selectedMicrophone', savedMicrophone);
        migrated = true;
      }
      if (
        savedAudioOutput &&
        savedAudioOutput !== '[object Object]' &&
        savedAudioOutput !== 'undefined' &&
        savedAudioOutput !== selectedAudioOutput
      ) {
        updateSetting('devices.selectedAudioOutput', savedAudioOutput);
        migrated = true;
      }

      // Mark migration as complete if anything was migrated
      if (migrated) {
        localStorage.setItem(migrationKey, 'true');
      } else {
        // Even if nothing needed migration, mark it as complete so we don't check again
        localStorage.setItem(migrationKey, 'true');
      }
    } catch {
      // Ignore storage access errors (e.g., privacy mode)
    }
  }, [selectedAudioOutput, selectedCamera, selectedMicrophone, updateSetting]);

  // Listen for device changes from Jitsi to sync back to Settings
  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: custom DOM event from Jitsi bridge
    const handleDeviceChange = (event: any) => {
      const { detail } = event;
      if (!detail || detail.source === 'settings') {
        return; // Ignore our own changes
      }

      console.log('[Settings] Received device change from Jitsi:', detail);

      try {
        // Update settings service from localStorage (which Jitsi updated)
        // biome-ignore lint/suspicious/noExplicitAny: device ID may be string or object from Jitsi
        const readId = (v: any) => (typeof v === 'string' ? v : `${v}` || '');
        if (detail.cameraId !== undefined) {
          const savedCamera =
            localStorage.getItem(DEVICE_LS_KEYS.camera) || readId(detail.cameraId);
          console.log('[Settings] Camera sync:', {
            savedCamera,
            currentCamera: selectedCamera,
            willUpdate: savedCamera && savedCamera !== selectedCamera,
          });
          if (savedCamera && savedCamera !== selectedCamera) {
            updateSetting('devices.selectedCamera', savedCamera);
          }
        }
        if (detail.microphoneId !== undefined) {
          const savedMicrophone =
            localStorage.getItem(DEVICE_LS_KEYS.microphone) || readId(detail.microphoneId);
          console.log('[Settings] Microphone sync:', {
            savedMicrophone,
            currentMicrophone: selectedMicrophone,
            willUpdate: savedMicrophone && savedMicrophone !== selectedMicrophone,
            detail: detail.microphoneId,
          });
          if (savedMicrophone && savedMicrophone !== selectedMicrophone) {
            console.log('[Settings] Updating microphone to:', savedMicrophone);
            updateSetting('devices.selectedMicrophone', savedMicrophone);
          }
        }
        if (detail.audioOutputId !== undefined) {
          const savedAudioOutput =
            localStorage.getItem(DEVICE_LS_KEYS.audioOutput) || readId(detail.audioOutputId);
          console.log('[Settings] Audio output sync:', {
            savedAudioOutput,
            currentOutput: selectedAudioOutput,
            willUpdate: savedAudioOutput && savedAudioOutput !== selectedAudioOutput,
          });
          if (savedAudioOutput && savedAudioOutput !== selectedAudioOutput) {
            updateSetting('devices.selectedAudioOutput', savedAudioOutput);
          }
        }
      } catch (e) {
        console.error('[Settings] Error syncing device change:', e);
      }
    };

    console.log('[Settings] Device change listener registered');
    window.addEventListener('settings-devices-changed', handleDeviceChange);
    return () => {
      console.log('[Settings] Device change listener removed');
      window.removeEventListener('settings-devices-changed', handleDeviceChange);
    };
  }, [selectedCamera, selectedMicrophone, selectedAudioOutput, updateSetting]);

  // Sync Settings device changes to Jitsi's localStorage
  const syncToJitsiLocalStorage = useCallback(async () => {
    try {
      // Mute flags must land even when no device is selected — prejoin reads
      // them from jitsiLocalStorage, not from sangoma-meet-settings.
      updateJitsiLocalStorageStartMuted({
        startWithAudioMuted: !!startWithAudioMuted,
        startWithVideoMuted: !!startWithVideoMuted,
      });

      // Don't sync if no devices are selected
      if (!(selectedCamera || selectedMicrophone || selectedAudioOutput)) {
        console.log('[Settings→Jitsi] Skipping sync - no devices selected');
        return;
      }

      console.log('[Settings→Jitsi] Starting sync with devices:', {
        camera: selectedCamera,
        microphone: selectedMicrophone,
        audioOutput: selectedAudioOutput,
      });

      // Enumerate devices to get labels
      if (!navigator.mediaDevices?.enumerateDevices) {
        console.warn('[Settings→Jitsi] MediaDevices API not available');
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === 'videoinput');
      const mics = devices.filter((d) => d.kind === 'audioinput');
      const spks = devices.filter((d) => d.kind === 'audiooutput');

      console.log('[Settings→Jitsi] Enumerated devices:', {
        cameras: cams.length,
        microphones: mics.length,
        speakers: spks.length,
      });

      // Get Jitsi localStorage
      const jitsiLs = localStorage.getItem('jitsiLocalStorage');
      // biome-ignore lint/suspicious/noExplicitAny: jitsiLocalStorage is untyped JSON
      let jitsiData: any = {};

      if (jitsiLs) {
        try {
          jitsiData = JSON.parse(jitsiLs);
        } catch (e) {
          console.warn('[Settings] Error parsing jitsiLocalStorage, creating new:', e);
          jitsiData = {};
        }
      }

      // Get or create base settings
      // biome-ignore lint/suspicious/noExplicitAny: Jitsi base settings parsed from JSON
      let baseSettings: any = {};
      if (jitsiData['features/base/settings']) {
        try {
          baseSettings = JSON.parse(jitsiData['features/base/settings']);
        } catch (e) {
          console.warn('[Settings] Error parsing base settings:', e);
        }
      }

      // Update device IDs and labels
      if (selectedCamera) {
        const cam = cams.find((d) => d.deviceId === selectedCamera);
        if (cam) {
          baseSettings.userSelectedCameraDeviceId = cam.deviceId;
          baseSettings.userSelectedCameraDeviceLabel = cam.label;
          console.log('[Settings→Jitsi] Synced camera:', cam.label);
        }
      }

      if (selectedMicrophone) {
        const mic = mics.find((d) => d.deviceId === selectedMicrophone);
        if (mic) {
          baseSettings.userSelectedMicDeviceId = mic.deviceId;
          baseSettings.userSelectedMicDeviceLabel = mic.label;
          console.log('[Settings→Jitsi] Synced microphone:', mic.label);
        }
      }

      if (selectedAudioOutput) {
        const spk = spks.find((d) => d.deviceId === selectedAudioOutput);
        if (spk) {
          baseSettings.userSelectedAudioOutputDeviceId = spk.deviceId;
          baseSettings.userSelectedAudioOutputDeviceLabel = spk.label;
          console.log('[Settings→Jitsi] Synced audio output:', spk.label);
        }
      }

      // Save back to Jitsi localStorage
      jitsiData['features/base/settings'] = JSON.stringify(baseSettings);
      localStorage.setItem('jitsiLocalStorage', JSON.stringify(jitsiData));

      // Note: Individual localStorage keys (settings.selectedCamera, etc.) are NOT updated here
      // They are persisted via settingsService when user clicks "Save" button
      // This ensures device changes are only saved on explicit user action

      console.log('[Settings→Jitsi] Updated Jitsi localStorage with device selections');
    } catch (e) {
      console.error('[Settings] Error syncing to Jitsi localStorage:', e);
    }
  }, [
    selectedCamera,
    selectedMicrophone,
    selectedAudioOutput,
    startWithAudioMuted,
    startWithVideoMuted,
  ]);

  // Device persistence is now handled by the settings service
  // Keep device change broadcasting for Jitsi integration
  useEffect(() => {
    try {
      const id = sanitizeDeviceId(selectedCamera);
      // Broadcast change so App/Jitsi can react immediately
      const event = new CustomEvent('settings-devices-changed', {
        detail: { source: 'settings', cameraId: id },
      });
      window.dispatchEvent(event);
      console.log('[Settings] Dispatched camera change event:', { cameraId: id });
      // Emit a secondary event for legacy listeners if any
      window.dispatchEvent(
        new CustomEvent('settings-devices-camera-changed', { detail: { cameraId: id } }),
      );
    } catch {
      // Ignore errors when dispatching events
    }
  }, [selectedCamera, sanitizeDeviceId]);

  useEffect(() => {
    try {
      const id = sanitizeDeviceId(selectedMicrophone);
      // Broadcast change event for real-time preview (no persistence until Save)
      const event = new CustomEvent('settings-devices-changed', {
        detail: { source: 'settings', microphoneId: id },
      });
      window.dispatchEvent(event);
      console.log('[Settings] Dispatched microphone change event:', { microphoneId: id });
      window.dispatchEvent(
        new CustomEvent('settings-devices-microphone-changed', { detail: { microphoneId: id } }),
      );
    } catch {
      // Ignore errors when dispatching events
    }
  }, [selectedMicrophone, sanitizeDeviceId]);

  useEffect(() => {
    try {
      const id = sanitizeDeviceId(selectedAudioOutput);
      const event = new CustomEvent('settings-devices-changed', {
        detail: { source: 'settings', audioOutputId: id },
      });
      window.dispatchEvent(event);
      console.log('[Settings] Dispatched audio output change event:', { audioOutputId: id });
      window.dispatchEvent(
        new CustomEvent('settings-devices-audiooutput-changed', { detail: { audioOutputId: id } }),
      );
    } catch {
      // Ignore errors when dispatching events
    }
  }, [selectedAudioOutput, sanitizeDeviceId]);

  // Note: Device changes are no longer auto-synced to localStorage
  // Persistence only happens when user clicks Save button

  /**
   * Push the cross-device meeting toggles once the local save has landed
   * (SM-1168). Failure is not fatal: the values are already persisted locally,
   * so this mirrors useSettingsMutation's "saved locally, not synchronized"
   * behavior rather than failing the whole save.
   */
  const syncMeetingPreferencesToBackend = async () => {
    if (!isBackendConnected) {
      return;
    }
    const local = getLocalMeetingPreferences();
    if (areMeetingPreferencesEqual(local, backendProfile?.meetings)) {
      return;
    }
    try {
      await updateBackendProfile({ meetings: local });
      console.log('[SettingsModal] Meeting preferences synced to backend');
    } catch (error) {
      console.warn('[SettingsModal] Meeting preferences saved locally only', error);
    }
  };

  const handleSave = async () => {
    try {
      clearErrors();

      // Persist a freshly accepted ToS. Unchecking is not permitted in the UI,
      // so the only valid transition here is unaccepted → accepted.
      if (localAccepted && !hasAcceptedTos) {
        setIsAccepting(true);
        try {
          await acceptTOS();
          await refetchTOS();
        } catch (error) {
          console.error('[Settings] Failed to update TOS acceptance:', error);
          setLocalAccepted(false);
          handleComponentError(error as Error, 'save_tos');
          return;
        } finally {
          setIsAccepting(false);
        }
      }

      // Save profile data to backend
      await handleProfileSave();

      // Validate and save all other settings (devices, calendar, meetings, terms)
      const settingsSuccess = await saveSettings();

      if (settingsSuccess) {
        // Sync device selections to Jitsi localStorage on successful save
        await syncToJitsiLocalStorage();
        await syncMeetingPreferencesToBackend();

        setShowSuccessMessage(true);

        // Hide success message after 2 seconds (modal stays open)
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 2000);

        console.log('Settings and profile saved successfully');
      } else {
        console.error('Failed to save settings. Validation errors:', errors);
      }
    } catch (error) {
      handleComponentError(error as Error, 'save_settings');
    }
  };

  const renderDevicesTab = () => (
    <DevicesTab
      isOpen={isOpen}
      activeTab={activeTab}
      selectedCamera={selectedCamera}
      onChangeSelectedCamera={(v) => updateSetting('devices.selectedCamera', sanitizeDeviceId(v))}
      selectedMicrophone={selectedMicrophone}
      onChangeSelectedMicrophone={(v) =>
        updateSetting('devices.selectedMicrophone', sanitizeDeviceId(v))
      }
      selectedAudioOutput={selectedAudioOutput}
      onChangeSelectedAudioOutput={(v) =>
        updateSetting('devices.selectedAudioOutput', sanitizeDeviceId(v))
      }
      startWithAudioMuted={startWithAudioMuted}
      onChangeStartWithAudioMuted={(checked) =>
        updateSetting('devices.startWithAudioMuted', checked)
      }
      startWithVideoMuted={startWithVideoMuted}
      onChangeStartWithVideoMuted={(checked) =>
        updateSetting('devices.startWithVideoMuted', checked)
      }
      onUserInteraction={() => setUserHasMadeChanges(true)}
    />
  );

  const renderProfileTab = () => (
    <div className="settingsTabContent">
      <div className="profileContent">
        {/* Profile Error */}
        {profileError && (
          <div className="profileError">
            <img
              src={warningIcon.src}
              width={warningIcon.width}
              height={warningIcon.height}
              alt="Error"
            />
            <span>{profileError?.message}</span>
          </div>
        )}

        {/* Profile Section */}
        <div className="profileSection">
          <h3 className="profileSectionTitle">Profile</h3>

          {/* Display Name */}
          <div className="profileField">
            <label className="profileFieldLabel" htmlFor="display-name-input">
              Display name
            </label>
            <div className="profileInputGroup">
              <input
                id="display-name-input"
                type="text"
                value={localProfile.displayName}
                onChange={(e) => handleProfileChange('displayName', e.target.value)}
                // biome-ignore lint/suspicious/noExplicitAny: errors.profile shape is not typed
                className={`profileInput ${(errors.profile as any)?.displayName ? 'error' : ''}`}
                placeholder="Enter your display name"
                maxLength={100}
                disabled={isLoadingProfile || isAuthenticated}
                readOnly={isAuthenticated}
              />
              {(errors.profile as Record<string, string>)?.displayName && (
                <div className="fieldError">
                  {(errors.profile as Record<string, string>).displayName}
                </div>
              )}
              {!isAuthenticated && (
                <button type="button" className="profileInputIcon">
                  <img
                    src={editIcon.src}
                    width={editIcon.width}
                    height={editIcon.height}
                    alt="Edit"
                  />
                </button>
              )}
            </div>
          </div>

          {/* Gravatar Email */}
          <div className="profileField">
            <label className="profileFieldLabel" htmlFor="gravatar-email-input">
              Email
            </label>
            <div className="profileInputGroup">
              <input
                id="gravatar-email-input"
                type="email"
                value={localProfile.gravatarEmail}
                onChange={(e) => handleProfileChange('gravatarEmail', e.target.value)}
                className={`profileInput ${(errors.profile as Record<string, string>)?.gravatarEmail ? 'error' : ''}`}
                placeholder="Enter your email"
                maxLength={254}
                disabled={isLoadingProfile || isAuthenticated}
                readOnly={isAuthenticated}
              />
              {(errors.profile as Record<string, string>)?.gravatarEmail && (
                <div className="fieldError">
                  {(errors.profile as Record<string, string>).gravatarEmail}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Current Plan Section */}
        {/* TODO: Re-implement license display with licensing API */}
        {false}
      </div>
    </div>
  );

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!isTrustedCalendarMessageOrigin(event.origin)) {
        return;
      }

      const { type, success, provider } = event.data || {};
      if (type === 'calendar-connected' && success) {
        if (provider === 'google' || provider === 'microsoft') {
          setCalendarReconnectErrors((prev) => ({ ...prev, [provider]: null }));
          try {
            sessionStorage.removeItem(`calendarReconnectError:${provider}`);
          } catch {
            // Ignore storage access errors
          }
        }

        refetchCalendarTypes();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [refetchCalendarTypes]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const providers: Array<'google' | 'microsoft'> = ['google', 'microsoft'];
    const nextErrors: { google: string | null; microsoft: string | null } = {
      google: null,
      microsoft: null,
    };

    for (const provider of providers) {
      try {
        nextErrors[provider] = sessionStorage.getItem(`calendarReconnectError:${provider}`);
      } catch {
        // Ignore storage access errors
      }
    }

    setCalendarReconnectErrors(nextErrors);
  }, [isOpen]);

  useEffect(() => {
    const handleCalendarFetchAuthError = (
      event: Event & {
        detail?: { provider?: string; message?: string };
      },
    ) => {
      const provider = event.detail?.provider;
      const message = event.detail?.message;
      if (!((provider === 'google' || provider === 'microsoft') && message)) {
        return;
      }
      setCalendarReconnectErrors((prev) => ({ ...prev, [provider]: message }));
    };

    window.addEventListener('calendar-fetch-auth-error', handleCalendarFetchAuthError);
    return () => {
      window.removeEventListener('calendar-fetch-auth-error', handleCalendarFetchAuthError);
    };
  }, []);

  // biome-ignore lint/suspicious/noExplicitAny: event handler and OAuth source object are not typed
  const handleSignIn = async (_e: any, key: string, source: any, shouldShowReconnect = false) => {
    const providerKey = key === 'google' || key === 'microsoft' ? (key as CalendarProvider) : null;
    let loginLink = source?.login_link;

    if (shouldShowReconnect && providerKey) {
      try {
        if (providerKey === 'google') {
          await calendarService.disconnectGoogleCalendarType();
        } else if (providerKey === 'microsoft') {
          await calendarService.disconnectMicrosoftCalendarType();
        }
        const refreshedConnections = await refetchCalendarTypes();
        loginLink = refreshedConnections.data?.[providerKey]?.login_link ?? loginLink;
        setCalendarReconnectErrors((prev) => ({ ...prev, [providerKey]: null }));
        try {
          sessionStorage.removeItem(`calendarReconnectError:${providerKey}`);
        } catch {
          // Ignore storage access errors
        }
        notifyCalendarOauthResult({
          type: 'calendar-disconnected',
          provider: providerKey,
          success: true,
        });
      } catch (error) {
        console.error('[SettingsModal] Silent disconnect before reconnect failed:', error);
      }
    }

    // biome-ignore lint/suspicious/noExplicitAny: window.open reference
    let win: any = null;
    if (providerKey && loginLink) {
      win = window.open(
        `${loginLink}${buildCalendarOauthCloseUrl(providerKey)}`,
        '_blank',
        'height=580,width=800',
      );
      console.log(`Connecting to ${key.charAt(0).toUpperCase() + key.slice(1)} Calendar...`);
    }

    const interval = setInterval(() => {
      if (win?.closed) {
        clearInterval(interval);
      }
    }, 1000);
  };

  // biome-ignore lint/suspicious/noExplicitAny: event handler not typed
  const handleDisconnect = async (_e: any, key: string) => {
    if (key === 'google') {
      try {
        await calendarService.disconnectGoogleCalendarType();
        refetchCalendarTypes();
        setCalendarReconnectErrors((prev) => ({ ...prev, google: null }));
        try {
          sessionStorage.removeItem('calendarReconnectError:google');
        } catch {
          // Ignore storage access errors
        }
        // Notify other parts of the app (e.g. CalendarRecentPanel) that a calendar was disconnected
        notifyCalendarOauthResult({
          type: 'calendar-disconnected',
          provider: 'google',
          success: true,
        });
      } catch (error) {
        handleComponentError(error as Error, 'google_disconnect');
      }
    } else if (key === 'microsoft') {
      try {
        await calendarService.disconnectMicrosoftCalendarType();
        refetchCalendarTypes();
        setCalendarReconnectErrors((prev) => ({ ...prev, microsoft: null }));
        try {
          sessionStorage.removeItem('calendarReconnectError:microsoft');
        } catch {
          // Ignore storage access errors
        }

        // Notify other parts of the app (e.g. CalendarRecentPanel) that a calendar was disconnected
        notifyCalendarOauthResult({
          type: 'calendar-disconnected',
          provider: 'microsoft',
          success: true,
        });
      } catch (error) {
        handleComponentError(error as Error, 'microsoft_disconnect');
      }
    }
  };

  const shouldShowCalendarReconnect = (
    provider: 'google' | 'microsoft',
    // biome-ignore lint/suspicious/noExplicitAny: calendar provider payload from API is runtime-typed
    providerData: any,
  ): boolean => {
    const fetchErrorMessage = calendarReconnectErrors[provider];
    const hasFetchPermissionOrCredentialError =
      typeof fetchErrorMessage === 'string' &&
      /(permission|forbidden|unauthorized|invalid credentials|authError|UNAUTHENTICATED|token expired|session expired|access denied|cpaas|third.?party|email|not able to fetch|fetch failed|reconnect)/i.test(
        fetchErrorMessage,
      );

    if (hasFetchPermissionOrCredentialError) {
      return true;
    }

    if (!providerData || typeof providerData !== 'object') {
      return false;
    }

    const errorText = [
      providerData.authError,
      providerData.error,
      providerData.message,
      providerData.detail,
    ]
      .filter((value): value is string => typeof value === 'string')
      .join(' ');
    const hasConnectionErrorMessage =
      /permission|forbidden|unauthorized|invalid credentials|authError|UNAUTHENTICATED|token expired|session expired|access denied|cpaas|third.?party|email|not able to fetch|fetch failed|reconnect/i.test(
        errorText,
      );

    const rawStatus = providerData.status ?? providerData.statusCode;
    const status = typeof rawStatus === 'string' ? Number(rawStatus) : rawStatus;
    const isAuthOrPermissionStatus = status === 401 || status === 403;

    return hasConnectionErrorMessage || isAuthOrPermissionStatus;
  };

  const getCalendarReconnectWarningMessage = (
    provider: 'google' | 'microsoft',
    // biome-ignore lint/suspicious/noExplicitAny: calendar provider payload from API is runtime-typed
    providerData: any,
  ): string => {
    const fetchErrorMessage = calendarReconnectErrors[provider];
    if (typeof fetchErrorMessage === 'string' && fetchErrorMessage.trim()) {
      return fetchErrorMessage.trim();
    }

    if (providerData && typeof providerData === 'object') {
      const firstProviderError = [
        providerData.authError,
        providerData.error,
        providerData.message,
        providerData.detail,
      ].find((value): value is string => typeof value === 'string' && value.trim().length > 0);

      if (firstProviderError) {
        return firstProviderError.trim();
      }
    }

    return `Please re-connect your ${getProviderDisplayName(provider)} Calendar to continue syncing events.`;
  };

  const renderCalendarTab = () => {
    const hasBlockingUnsavedChanges = userHasMadeChanges && hasUnsavedChanges && !isSaving;
    return (
      <div className="settingsTabContent">
        <div className="calendarContent">
          <div className="calendarSection">
            <h3 className="calendarSectionTitle">Calendar Integration</h3>
            <p className="calendarDescription">
              The Sangoma Meet calendar integration is used to securely access your calendar so it
              can read upcoming events.
            </p>

            {hasBlockingUnsavedChanges && (
              <p className="calendarActionLockMessage">
                Please save your changes before proceeding.
              </p>
            )}

            <div className="calendarProviders">
              {/* biome-ignore lint/suspicious/noExplicitAny: calendarTypes entries are not typed */}
              {Object.entries(calendarTypes ?? {}).map(([provider, data]: any) => {
                const isConnected = data.connected;
                const providerKey =
                  provider === 'google' || provider === 'microsoft' ? provider : null;
                const shouldShowReconnect =
                  isConnected && !!providerKey && shouldShowCalendarReconnect(providerKey, data);
                const reconnectWarningMessage =
                  shouldShowReconnect && providerKey
                    ? getCalendarReconnectWarningMessage(providerKey, data)
                    : null;
                const email = data.email;
                let providerContent: React.ReactElement;

                if (!isConnected) {
                  providerContent = (
                    <button
                      type="button"
                      onClick={(e) => handleSignIn(e, provider, data)}
                      className={`${getSignInButtonClasses(provider)} calendarSignInButtonWide`}
                      disabled={hasBlockingUnsavedChanges}
                    >
                      <img
                        src={getProviderIcon(provider)?.src}
                        width={getProviderIcon(provider)?.width}
                        height={getProviderIcon(provider)?.height}
                        alt={provider}
                        className="calendarProviderIcon"
                      />
                      <span className="calendarSignInButtonLabel">
                        Connect with {getProviderDisplayName(provider)}
                      </span>
                    </button>
                  );
                } else if (shouldShowReconnect) {
                  providerContent = (
                    <div className="calendarReconnectWrapper">
                      <div
                        className={`calendarConnected calendarReconnectState ${hasBlockingUnsavedChanges ? 'calendarConnectedDisabled' : ''}`}
                      >
                        <div className="connectedInfo">
                          <img
                            src={getProviderIcon(provider)?.src}
                            width={getProviderIcon(provider)?.width}
                            height={getProviderIcon(provider)?.height}
                            alt={provider}
                            className="calendarProviderIcon"
                          />

                          <div className="connectedDetails">
                            <span className="connectedLabel">
                              Connected to {getProviderDisplayName(provider)} Calendar
                            </span>
                            <span className="connectedEmail">{email || '—'}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => handleSignIn(e, provider, data, true)}
                          className="disconnectButton reconnectWarningButton"
                          disabled={hasBlockingUnsavedChanges}
                        >
                          Re-connect
                        </button>
                      </div>
                      <div className="calendarReconnectWarning">
                        <img
                          src={warningIcon.src}
                          width={warningIcon.width}
                          height={warningIcon.height}
                          alt="Warning"
                          className="calendarReconnectWarningIcon"
                        />
                        <span className="calendarReconnectWarningText">
                          {reconnectWarningMessage}
                        </span>
                      </div>
                    </div>
                  );
                } else {
                  providerContent = (
                    <div
                      className={`calendarConnected ${hasBlockingUnsavedChanges ? 'calendarConnectedDisabled' : ''}`}
                    >
                      <div className="connectedInfo">
                        <img
                          src={getProviderIcon(provider)?.src}
                          width={getProviderIcon(provider)?.width}
                          height={getProviderIcon(provider)?.height}
                          alt={provider}
                          className="calendarProviderIcon"
                        />
                        <div className="connectedDetails">
                          <span className="connectedLabel">
                            Connected to {getProviderDisplayName(provider)} Calendar
                          </span>
                          <span className="connectedEmail">{email || '—'}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDisconnect(e, provider)}
                        className="disconnectButton"
                        disabled={hasBlockingUnsavedChanges}
                      >
                        Disconnect
                      </button>
                    </div>
                  );
                }

                return (
                  <div className="calendarProvider" key={provider}>
                    {providerContent}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMeetingsTab = () => (
    <div className="settingsTabContent">
      <div className="meetingsContent">
        <div className="meetingsSection">
          <h3 className="meetingsSectionTitle">Customize meeting</h3>

          <div className="meetingOptions">
            {/* Enable pre meeting screen */}
            <div className="meetingOption">
              <div className="meetingOptionContent">
                <div className="meetingOptionLabel">Enable pre meeting screen</div>
              </div>
              <label className="toggleSwitch">
                <input
                  type="checkbox"
                  checked={!!settings?.meetings?.['prejoinConfig.enabled']}
                  onChange={(e) => {
                    setUserHasMadeChanges(true);
                    updateSetting(`meetings.${'prejoinConfig.enabled'}`, e.target.checked);
                  }}
                />
                <span className="toggleSlider" />
              </label>
            </div>

            {/* Enable keyboard shortcuts */}
            <div className="meetingOption">
              <div className="meetingOptionContent">
                <div className="meetingOptionLabel">Enable keyboard shortcuts</div>
              </div>
              <label className="toggleSwitch">
                <input
                  type="checkbox"
                  checked={!settings?.meetings?.disableShortcuts}
                  onChange={(e) => {
                    setUserHasMadeChanges(true);
                    updateSetting(`meetings.${'disableShortcuts'}`, !e.target.checked);
                  }}
                />
                <span className="toggleSlider" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleReadTerms = () => {
    window.open(TOS_PDF_URL, '_blank', 'noopener,noreferrer');
    setHasViewedTos(true);
  };

  // Format the TOS acceptance date for display
  const formatTosDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
      return 'Never';
    }

    try {
      const date = new Date(dateString);
      // Format as: "January 15, 2024 at 3:45 PM"
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (_error) {
      return 'Invalid date';
    }
  };

  const handleTosCheckboxChange = (checked: boolean) => {
    setLocalAccepted(checked);
    setUserHasMadeChanges(true);
  };

  const renderTermsTab = () => {
    // Once the ToS is accepted on the backend the checkbox locks: re-acceptance is
    // only triggered by a ToS update (which clears `hasAcceptedTos` server-side).
    // Before that, the user must open the document at least once.
    const isCheckboxDisabled =
      isLoadingTOS || isAccepting || isSaving || hasAcceptedTos || !hasViewedTos;

    return (
      <div className="settingsTabContent">
        <div className="termsContent">
          <div className="termsSection">
            <h3 className="termsSectionTitle">Terms and Conditions</h3>

            <p className="termsDescription">
              To continue using Sangoma Meet, you must review and accept our Terms of Service (TOS).
              By checking the box, you are confirming our terms of service.
            </p>

            <div className="termsInfo">
              <p className="termsLastAccepted">
                Last accepted: {isLoadingTOS ? 'Loading...' : formatTosDate(acceptedDate)}
              </p>

              <button type="button" onClick={handleReadTerms} className="termsReadButton">
                <img
                  src={downloadIcon.src}
                  width={downloadIcon.width}
                  height={downloadIcon.height}
                  alt="PDF"
                  className="termsIcon"
                />
                <span>Read Terms and condition</span>
              </button>

              <div className="termsCheckboxContainer">
                <label className="termsCheckboxLabel">
                  <input
                    type="checkbox"
                    checked={localAccepted}
                    onChange={(e) => handleTosCheckboxChange(e.target.checked)}
                    className="termsCheckbox"
                    id="settings-tos-agreement"
                    disabled={isCheckboxDisabled}
                  />
                  <span className="checkboxCustom" />
                  <span className="checkboxText">I agree to the terms and conditions</span>
                </label>
                {!(hasAcceptedTos || hasViewedTos) && (
                  <p className="termsCheckboxHint">
                    Please read the terms and conditions before accepting
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'devices':
        return renderDevicesTab();
      case 'profile':
        return renderProfileTab();
      case 'calendar':
        return renderCalendarTab();
      case 'meetings':
        return renderMeetingsTab();
      case 'terms':
        return renderTermsTab();
      default:
        return renderDevicesTab();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="settingsModalOverlay">
      <div className="settingsModal" ref={modalRef}>
        {/* Header */}
        <div className="settingsHeader">
          <h2 className="settingsTitle">Settings</h2>
          <button type="button" onClick={handleCancel} className="settingsCloseButton">
            <img
              src={closeIcon.src}
              width={closeIcon.width}
              height={closeIcon.height}
              alt="Close"
            />
          </button>
        </div>

        {/* Content */}
        <div className="settingsContent">
          {/* Sidebar */}
          <div className="settingsSidebar">
            <button
              type="button"
              onClick={() => setActiveTab('devices')}
              className={`sidebarTab ${activeTab === 'devices' ? 'active' : ''}`}
            >
              Devices
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('profile')}
              className={`sidebarTab ${activeTab === 'profile' ? 'active' : ''}`}
            >
              Profile
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('calendar')}
              className={`sidebarTab ${activeTab === 'calendar' ? 'active' : ''}`}
            >
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('meetings')}
              className={`sidebarTab ${activeTab === 'meetings' ? 'active' : ''}`}
            >
              Meetings
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('terms')}
              className={`sidebarTab ${activeTab === 'terms' ? 'active' : ''}`}
            >
              Terms and conditions
            </button>
          </div>

          {/* Main Content */}
          <div className="settingsMainContent">{renderTabContent()}</div>
        </div>

        {/* Footer */}
        <div className="settingsFooter">
          {/* Status Messages */}
          {showSuccessMessage && (
            <div className="statusMessage success">
              <img
                src={checkIcon.src}
                width={checkIcon.width}
                height={checkIcon.height}
                alt="Success"
              />
              Changes saved
            </div>
          )}

          {showErrorMessage && (
            <div className="statusMessage error">
              <img
                src={warningIcon.src}
                width={warningIcon.width}
                height={warningIcon.height}
                alt="Error"
              />
              Please fix the errors above
            </div>
          )}

          {userHasMadeChanges && hasUnsavedChanges && !isSaving && (
            <div className="statusMessage warning">
              <img
                src={warningIcon.src}
                width={warningIcon.width}
                height={warningIcon.height}
                alt="Warning"
              />
              You have unsaved changes
            </div>
          )}

          {/* Action Buttons */}
          <div className="footerActions">
            <button
              type="button"
              onClick={handleCancel}
              className="settingsCancelButton"
              disabled={isSaving}
            >
              CANCEL
            </button>

            <button
              type="button"
              onClick={handleSave}
              className={`settingsSaveButton ${!(userHasMadeChanges || hasTosChanges) ? 'disabled' : ''}`}
              disabled={!(userHasMadeChanges || hasTosChanges) || isSaving || isLoading}
            >
              {isSaving ? (
                <>
                  <span className="saveSpinner" />
                  SAVING...
                </>
              ) : (
                'SAVE'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { SettingsModal };
